import React, { useState, useEffect, useRef } from 'react';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { agentAuthHeaders } from '../utils/functionAuth';
import { getApiUrl, isNativeApp } from '../utils/platform';
import { showToast } from './Toast';
import { canDeleteVendor, canManageVendor, footnoteName } from '../shared/vendorConsent';
import { buildVendorUpdateData } from '../shared/vendorUpdate';
import { formatPhoneDisplay } from '../shared/phoneFormat';
import {
  SETTLEMENT_VENDOR_NOTICE,
  isSettlementServiceCategory,
  isSettlementServiceVendor,
  categoriesForVendorForm,
  categoryBubbles,
  missingDefaultCategoryNames,
  sortVendorCategories,
  withDefaultVendorCategories,
} from '../shared/vendorCategories';
import {
  BEFORE_YOU_HIRE,
  BeforeYouHireModal,
  VENDOR_ATTESTATION,
  VendorAttestationField,
  VendorCardFootnote,
  attestationAccepted,
  postVendorConsent,
  useVendorHireGate,
} from './VendorConsent';
import { REGIONAL_VENDORS_ENABLED } from '../shared/featureFlags';
import { vendorInviteMessageForProfile } from '../shared/vendorInviteMessage';
import { vendorInCategory, vendorMatchesTrade } from '../shared/vendorTrades';
import HomeownerDiscount from './HomeownerDiscount';
import { VendorCategorySelect, VendorTapCard, callControlStyle } from './TapToExpand';
import { ServiceAreaFields, ServesLine } from './ServiceAreaFields';
import { formatServes, serviceAreaFromInput } from '../shared/serviceArea';
import { AdoptNotice, ClientProsSection } from './HomeownerPros';
import {
  ADDED_BY_CLIENTS,
  ADDED_BY_CLIENTS_VALUE,
  AGENT_PRO_COLUMNS,
  AGENT_YOUR_VENDORS_HELP,
  EMPTY_ADD_VENDOR,
  EMPTY_TEAM_VENDORS,
  TEAM_VENDORS,
  TEAM_VENDORS_HELP,
  YOUR_VENDORS,
  adoptVendorPayload,
  alreadyAdopted,
  shapeProsForAgent,
} from '../shared/homeownerPros';
import { vendorSoftCardStyle, vendorSoftShadow } from '../shared/vendorCardStyle';
import { stripVendorListingDisclaimer } from '../shared/vendorDescription';
import VendorCardCopy from './VendorCardCopy';
import VendorImport from './VendorImport';
import ConfirmDialog from './ConfirmDialog';
import {
  includePendingRows,
  projectRows,
  vendorCardFromApplication,
  withoutDeclinedVendors,
} from '../shared/listRefresh';

const CATEGORY_SEED_VERSION = 'home-services-1';

function categorySeedKey(agentId) {
  return `pacarion.vendorCategorySeed.${agentId}`;
}

function readCategorySeed(agentId) {
  try {
    return window.localStorage.getItem(categorySeedKey(agentId)) === CATEGORY_SEED_VERSION;
  } catch (error) {
    return false;
  }
}

function writeCategorySeed(agentId) {
  try {
    window.localStorage.setItem(categorySeedKey(agentId), CATEGORY_SEED_VERSION);
  } catch (error) {
    // The list still shows. The next visit can try the save again.
  }
}

const VendorManagement = ({ profile, onStatsUpdate }) => {
  const { colors, isDark } = useTheme();
  const [vendors, setVendors] = useState([]);
  const [creatorNames, setCreatorNames] = useState({});
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [areaError, setAreaError] = useState('');
  const [applications, setApplications] = useState([]);
  const [openApplicationId, setOpenApplicationId] = useState('');
  const [applicationBusyId, setApplicationBusyId] = useState('');
  const applicationBusy = useRef(new Set());
  const nameInputRef = useRef(null);
  const savedVendorPatches = useRef(new Map());
  const removedVendorIds = useRef(new Set());
  const pendingVendors = useRef(new Map());
  const [declineApplication, setDeclineApplication] = useState(null);

  // Regional vendors state
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'regional'
  const [regionalVendors, setRegionalVendors] = useState([]);
  const [regionalRegionFilter, setRegionalRegionFilter] = useState('');
  const [regionalCategoryFilter, setRegionalCategoryFilter] = useState('all');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [vendorAnalytics, setVendorAnalytics] = useState([]);
  const [expandedVendorId, setExpandedVendorId] = useState(null);
  const [clientPros, setClientPros] = useState([]);
  const [adoptedProId, setAdoptedProId] = useState(null);
  const [attested, setAttested] = useState(false);
  const pendingVendorId = useRef(null);
  const hireGate = useVendorHireGate(
    async () => {
      const data = await postVendorConsent(await agentAuthHeaders(), { action: 'status' });
      return data.accepted === true;
    },
    async (pending) => {
      await postVendorConsent(await agentAuthHeaders(), {
        action: 'accept',
        consent_type: BEFORE_YOU_HIRE,
        vendor_id: pending && pending.vendorId ? pending.vendorId : null,
      });
    },
  );
  const [formData, setFormData] = useState({
    name: '',
    categories: [], // Changed from single category to array for multiple categories
    type: 'maintenance',
    phone: '',
    email: '',
    website: '',
    discount: '',
    code: '',
    description: '',
    visibility: 'personal', // 'personal' or 'team'
    service_county_fips: [],
    service_statewide: [],
  });

  useEffect(() => {
    loadVendors();
    loadClientPros();
    loadCategories();
    if (REGIONAL_VENDORS_ENABLED) loadRegionalVendors();
    loadApplications();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadApplications = async () => {
    if (!profile?.id) return;
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/vendor-application-approve'), {
        headers: await agentAuthHeaders(),
      });
      const result = await response.json();
      if (response.ok) setApplications(result.applications || []);
    } catch (error) {
      console.error('Error loading vendor applications:', error);
    }
  };

  const createVendorInvite = async () => {
    const response = await fetch(getApiUrl('/.netlify/functions/vendor-invite'), {
      method: 'POST',
      headers: await agentAuthHeaders(),
    });
    const result = await response.json();
    if (!response.ok) {
      showToast(result.error || 'Could not create an invite');
      return;
    }
    setInviteCopied(false);
    setInviteUrl(result.url);
  };

  const copyInviteLink = async () => {
    const message = vendorInviteMessageForProfile(profile, inviteUrl);
    try {
      await navigator.clipboard.writeText(message);
      setInviteCopied(true);
      showToast(message, 'success', 8000);
    } catch (error) {
      showToast(message);
    }
  };

  const shareInviteLink = async () => {
    const message = vendorInviteMessageForProfile(profile, inviteUrl);
    try {
      await navigator.share({
        title: 'Pacarion vendor invite',
        text: message,
        url: inviteUrl,
      });
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      await copyInviteLink();
    }
  };

  const decideApplication = async (id, action) => {
    if (!id || applicationBusy.current.has(id)) return;
    applicationBusy.current.add(id);
    setApplicationBusyId(id);
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/vendor-application-approve'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ id, action }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        showToast(result.error || (action === 'decline' ? 'Could not decline this application' : 'Could not accept this application'));
        return;
      }
      const application = applications.find((row) => row && row.id === id) || null;
      setApplications((current) => current.filter((row) => row.id !== id));
      setOpenApplicationId((current) => (current === id ? '' : current));
      if (action === 'decline') {
        const removedVendorId = (result && result.removedVendorId) || (application && application.vendor_id) || '';
        if (removedVendorId) {
          removedVendorIds.current.add(removedVendorId);
          pendingVendors.current.delete(removedVendorId);
        }
        setVendors((current) => withoutDeclinedVendors(
          current,
          application,
          removedVendorId,
          removedVendorIds.current,
        ));
      } else if (application && result && result.id) {
        const card = vendorCardFromApplication(application, profile.id, result.id);
        pendingVendors.current.set(result.id, card);
        removedVendorIds.current.delete(result.id);
        setVendors((current) => includePendingRows(
          current.filter((row) => row.id !== result.id),
          pendingVendors.current,
        ));
      }
      await Promise.all([loadApplications(), loadVendors()]);
    } finally {
      applicationBusy.current.delete(id);
      setApplicationBusyId((current) => (current === id ? '' : current));
    }
  };

  const loadVendors = async () => {
    if (!profile?.id) return;

    try {
      const namesPromise = loadCreatorNames();
      const query = profile.team_id
        ? supabase
          .from('vendors')
          .select('*')
          .or(`and(agent_id.eq.${profile.id},visibility.eq.personal),and(team_id.eq.${profile.team_id},visibility.eq.team)`)
          .order('date_added', { ascending: false })
        : supabase
          .from('vendors')
          .select('*')
          .eq('agent_id', profile.id)
          .order('date_added', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      const rows = includePendingRows(data || [], pendingVendors.current);
      setVendors(projectRows(rows, savedVendorPatches.current, removedVendorIds.current));
      await namesPromise;
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadClientPros = async () => {
    if (!profile?.id) return;
    try {
      const { data, error } = await supabase
        .from('homeowner_pros')
        .select(AGENT_PRO_COLUMNS);
      if (error) throw error;
      setClientPros(shapeProsForAgent(data || []));
    } catch (error) {
      console.error('Error loading client providers:', error);
    }
  };

  const loadCreatorNames = async () => {
    const names = {};
    if (profile?.id) {
      names[profile.id] = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
    }
    if (profile?.team_id) {
      const { data, error } = await supabase.rpc('team_member_profiles');
      if (error) {
        console.error('Error loading vendor creators:', error);
      } else {
        (data || []).forEach((row) => {
          names[row.id] = [row.first_name, row.last_name].filter(Boolean).join(' ');
        });
      }
    }
    setCreatorNames(names);
  };

  const loadCategories = async () => {
    if (!profile?.id) return;

    try {
      const { data, error } = await supabase
        .from('vendor_categories')
        .select('*')
        .eq('agent_id', profile.id)
        .order('name');

      if (error) throw error;

      const rows = data || [];
      const missing = missingDefaultCategoryNames(rows);
      const seeded = readCategorySeed(profile.id);
      if (missing.length > 0 && !seeded) {
        const { error: insertError } = await supabase
          .from('vendor_categories')
          .insert(missing.map((name) => ({ agent_id: profile.id, name })));
        if (!insertError) {
          const again = await supabase
            .from('vendor_categories')
            .select('*')
            .eq('agent_id', profile.id)
            .order('name');
          if (!again.error && again.data && missingDefaultCategoryNames(again.data).length === 0) {
            writeCategorySeed(profile.id);
            setCategories(sortVendorCategories(again.data));
            return;
          }
        }
        setCategories(withDefaultVendorCategories(rows));
        return;
      }

      setCategories(sortVendorCategories(rows));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadRegionalVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('regional_vendors')
        .select('*')
        .eq('is_active', true)
        .order('region', { ascending: true })
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setRegionalVendors(data || []);
    } catch (error) {
      console.error('Error loading regional vendors:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that at least one category is selected
    if (!formData.categories || formData.categories.length === 0) {
      setCategoryError(true);
      return;
    }
    setCategoryError(false);
    const area = serviceAreaFromInput(formData);
    if (area.error) {
      setAreaError(area.error);
      return;
    }
    setAreaError('');
    if (!attestationAccepted(attested)) {
      showToast('Read the vendor attestation before saving.');
      return;
    }

    try {
      const typedName = nameInputRef.current && typeof nameInputRef.current.value === 'string'
        ? nameInputRef.current.value
        : formData.name;
      const trimmedName = String(typedName || '').trim();
      if (!trimmedName) {
        showToast('Enter a vendor name.');
        return;
      }
      const payload = {
        ...formData,
        name: trimmedName,
        description: stripVendorListingDisclaimer(formData.description),
      };
      let savedVendorId = editingVendor ? editingVendor.id : pendingVendorId.current;
      if (editingVendor) {
        const updateData = buildVendorUpdateData(payload, profile);
        updateData.name = trimmedName;
        const { data: saved, error } = await supabase
          .from('vendors')
          .update(updateData)
          .eq('id', editingVendor.id)
          .select('id, name')
          .maybeSingle();

        if (error) throw error;
        if (!saved || saved.name !== trimmedName) {
          showToast('The name did not save. Try again.');
          return;
        }
      } else if (!savedVendorId) {
        const vendorData = {
          agent_id: profile.id,
          created_by: profile.id,
          ...buildVendorUpdateData(payload, profile),
          type: 'maintenance',
        };
        if (adoptedProId) {
          const adopted = adoptVendorPayload(
            clientPros.find((pro) => pro.id === adoptedProId) || { id: adoptedProId, name: formData.name, trade: formData.categories[0] },
            { ...profile, visibility: formData.visibility }
          );
          if (adopted) {
            vendorData.adopted_from_homeowner_pro_id = adopted.adopted_from_homeowner_pro_id;
            vendorData.name = adopted.name;
            vendorData.phone = formData.phone || adopted.phone;
            vendorData.email = formData.email || adopted.email;
            vendorData.website = formData.website || adopted.website;
            vendorData.description = payload.description || '';
          }
        }

        const { data, error } = await supabase
          .from('vendors')
          .insert([vendorData])
          .select('id')
          .single();

        if (error) throw error;
        savedVendorId = data.id;
        pendingVendorId.current = data.id;
      }

      await postVendorConsent(await agentAuthHeaders(), {
        action: 'accept',
        consent_type: VENDOR_ATTESTATION,
        vendor_id: savedVendorId,
      });
      if (editingVendor) {
        savedVendorPatches.current.set(editingVendor.id, { name: trimmedName });
        setVendors((rows) => projectRows(rows, savedVendorPatches.current, removedVendorIds.current));
      }
      showToast(editingVendor ? 'Vendor updated successfully!' : formData.visibility === 'team' ? 'Added to team vendors.' : 'Added to your vendors.');

      pendingVendorId.current = null;
      setShowModal(false);
      setEditingVendor(null);
      setAdoptedProId(null);
      setAttested(false);
      setFormData({
        name: '',
        categories: [], // Reset to empty array for multiple categories
        type: 'maintenance',
        phone: '',
        email: '',
        website: '',
        discount: '',
        code: '',
        description: '',
        visibility: 'personal',
        service_county_fips: [],
        service_statewide: [],
      });

      await Promise.all([
        loadVendors(),
        onStatsUpdate ? Promise.resolve(onStatsUpdate()) : null,
      ]);
    } catch (error) {
      console.error('Error saving vendor:', error);
      if (error && (error.code === '23505' || /duplicate key/i.test(error.message || ''))) {
        showToast('This provider is already in your vendors.');
        return;
      }
      showToast(`Error saving vendor: ${error.message || 'Please try again.'}`);
    }
  };

  const openAdopt = (pro) => {
    if (!pro || alreadyAdopted(pro.id, vendors)) return;
    const payload = adoptVendorPayload(pro, profile);
    if (!payload) return;
    setEditingVendor(null);
    setAdoptedProId(pro.id);
    setFormData({
      name: payload.name,
      categories: payload.categories,
      type: 'maintenance',
      phone: payload.phone || '',
      email: payload.email || '',
      website: payload.website || '',
      discount: '',
      code: '',
      description: '',
      visibility: 'personal',
      service_county_fips: pro.service_county_fips || [],
      service_statewide: pro.service_statewide || [],
    });
    setAttested(false);
    setCategoryError(false);
    setAreaError('');
    pendingVendorId.current = null;
    setShowModal(true);
  };

  const handleEdit = (vendor) => {
    setAdoptedProId(null);
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      categories: categoriesForVendorForm(vendor),
      type: vendor.type,
      phone: vendor.phone,
      email: vendor.email || '',
      website: vendor.website || '',
      discount: vendor.discount || '',
      code: vendor.code || '',
      description: stripVendorListingDisclaimer(vendor.description || ''),
      visibility: vendor.visibility || 'personal',
      service_county_fips: vendor.service_county_fips || [],
      service_statewide: vendor.service_statewide || [],
    });
    setAttested(false);
    setCategoryError(false);
    setAreaError('');
    pendingVendorId.current = null;
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', id);

      if (error) throw error;

      removedVendorIds.current.add(id);
      savedVendorPatches.current.delete(id);
      setVendors((rows) => projectRows(rows, savedVendorPatches.current, removedVendorIds.current));
      await Promise.all([
        loadVendors(),
        onStatsUpdate ? Promise.resolve(onStatsUpdate()) : null,
      ]);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      showToast('Error deleting vendor. Please try again.');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showToast('Please enter a category name');
      return;
    }
    if (isSettlementServiceCategory(newCategoryName)) {
      showToast("Pacarion doesn't list settlement-service providers.");
      return;
    }

    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('vendor_categories')
          .update({ name: newCategoryName.trim() })
          .eq('id', editingCategory.id);

        if (error) throw error;
        showToast('Category updated successfully!');
      } else {
        const { error } = await supabase
          .from('vendor_categories')
          .insert([{
            agent_id: profile.id,
            name: newCategoryName.trim()
          }]);

        if (error) throw error;
        showToast('Category added successfully!');
      }

      setNewCategoryName('');
      setEditingCategory(null);
      setShowCategoryModal(false);
      await loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      if (error.code === '23505') {
        showToast('This category already exists!');
      } else {
        showToast('Error saving category. Please try again.');
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? Vendors using this category will not be affected.')) {
      return;
    }
    if (String(id || '').startsWith('default:')) {
      setCategories((current) => current.filter((category) => category.id !== id));
      return;
    }

    try {
      const { error } = await supabase
        .from('vendor_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast('Error deleting category. Please try again.');
    }
  };

  const trackVendorClick = async (vendor, clickType) => {
    try {
      const { error } = await supabase
        .from('vendor_clicks')
        .insert([{
          vendor_id: vendor.id,
          agent_id: profile.id,
          click_type: clickType,
          vendor_name: vendor.name,
          vendor_category: vendor.category || (vendor.categories && vendor.categories[0]) || 'Uncategorized', // Backwards compatible
          vendor_categories: vendor.categories || (vendor.category ? [vendor.category] : []), // New array format
          user_agent: navigator.userAgent
        }]);

      if (error) {
        console.error('Error tracking vendor click:', error);
        // Don't show error to user - tracking is non-critical
      }
    } catch (error) {
      console.error('Error tracking vendor click:', error);
      // Silent fail - don't interrupt user experience
    }
  };

  const loadVendorAnalytics = async () => {
    if (!profile?.id) return;

    try {
      // Get all vendor clicks for this agent - same as Dashboard.js
      const { data: clicks, error } = await supabase
        .from('vendor_clicks')
        .select('*')
        .eq('agent_id', profile.id);

      if (error) throw error;

      // Group clicks by vendor
      const vendorClickMap = {};
      clicks?.forEach(click => {
        if (!vendorClickMap[click.vendor_id]) {
          vendorClickMap[click.vendor_id] = {
            vendor_id: click.vendor_id,
            vendor_name: click.vendor_name,
            category: click.vendor_category,
            total_clicks: 0,
            phone_clicks: 0,
            email_clicks: 0,
            website_clicks: 0
          };
        }
        vendorClickMap[click.vendor_id].total_clicks++;
        if (click.click_type === 'phone') vendorClickMap[click.vendor_id].phone_clicks++;
        if (click.click_type === 'email') vendorClickMap[click.vendor_id].email_clicks++;
        if (click.click_type === 'website') vendorClickMap[click.vendor_id].website_clicks++;
      });

      // Convert to array
      let analyticsArray = Object.values(vendorClickMap);

      // Join with vendors table to get current vendor names
      const vendorIds = analyticsArray.map(v => v.vendor_id);
      if (vendorIds.length > 0) {
        const { data: vendorData, error: vendorError } = await supabase
          .from('vendors')
          .select('id, name, category, categories')
          .in('id', vendorIds);

        if (!vendorError && vendorData) {
          // Update analytics with current vendor info
          analyticsArray = analyticsArray.map(analytics => {
            const vendor = vendorData.find(v => v.id === analytics.vendor_id);
            if (vendor) {
              return {
                ...analytics,
                vendor_name: vendor.name,
                category: vendor.categories && vendor.categories.length > 0
                  ? vendor.categories.join(', ')
                  : vendor.category || 'Uncategorized'
              };
            }
            return analytics;
          });
        }
      }

      // Sort by total clicks
      analyticsArray.sort((a, b) => b.total_clicks - a.total_clicks);
      setVendorAnalytics(analyticsArray);
    } catch (error) {
      console.error('Error loading vendor analytics:', error);
    }
  };

  const showingClientPros = activeFilter === ADDED_BY_CLIENTS_VALUE;
  const adoptedProIds = new Set(
    vendors.map((vendor) => vendor && vendor.adopted_from_homeowner_pro_id).filter(Boolean)
  );
  // First filter by category (supports both old single category and new categories array)
  let filteredVendors = (activeFilter === 'all' || showingClientPros)
    ? vendors
    : vendors.filter((vendor) => vendorMatchesTrade(vendor, activeFilter));

  const onTeam = Boolean(profile?.team_id);
  const yourVendors = onTeam
    ? filteredVendors.filter((vendor) => vendor.visibility !== 'team')
    : filteredVendors;
  const teamVendors = onTeam
    ? filteredVendors.filter((vendor) => vendor.visibility === 'team')
    : [];

  // Filter regional vendors
  let filteredRegionalVendors = regionalVendors;
  if (regionalRegionFilter) {
    filteredRegionalVendors = filteredRegionalVendors.filter(v => v.region === regionalRegionFilter);
  }
  if (regionalCategoryFilter && regionalCategoryFilter !== 'all') {
    filteredRegionalVendors = filteredRegionalVendors.filter((vendor) => vendorInCategory(vendor, regionalCategoryFilter));
  }

  // Get unique regions from regional vendors. Categories come from the shared list.
  const uniqueRegions = [...new Set(regionalVendors.map(v => v.region))].sort();
  const { offered: offeredCategories, extras: extraSelectedCategories } = categoryBubbles(categories, formData.categories);
  const categoryChips = offeredCategories.map((cat) => ({
    key: cat.id || cat.name,
    name: cat.name,
  })).concat(extraSelectedCategories.map((name) => ({
    key: `extra:${name}`,
    name,
  })));
  const categoryNeedle = categoryQuery.trim().toLowerCase();
  const visibleCategoryChips = categoryChips.filter((cat) => {
    if (!categoryNeedle) return true;
    return formData.categories.includes(cat.name) || cat.name.toLowerCase().includes(categoryNeedle);
  });

  const renderVendorCard = (vendor) => {
            const expanded = expandedVendorId === vendor.id;
            return (
            <div key={vendor.id} style={vendorSoftCardStyle(colors, isDark)}>
              <VendorTapCard
                vendor={vendor}
                expanded={expanded}
                onToggle={() => setExpandedVendorId(expanded ? null : vendor.id)}
                accent={colors.primary}
                contact={vendor.phone ? (
                  <a
                    href={`tel:${vendor.phone}`}
                    aria-label={`Call ${vendor.name}`}
                    onClick={(event) => hireGate.requestContact(event, {
                      href: event.currentTarget.href,
                      vendorId: vendor.id,
                      kind: 'phone',
                      onAllowed: () => trackVendorClick(vendor, 'phone'),
                    })}
                    style={callControlStyle(colors.primary, colors.border)}
                  >
                    Call
                  </a>
                ) : null}
              >
                <ServesLine
                  record={vendor}
                  missingPrompt={canManageVendor(profile, vendor)}
                  onAdd={() => handleEdit(vendor)}
                />
                <VendorCardCopy description={vendor.description} color={colors.textSecondary} />
                {vendor.phone ? (
                  <p style={{ margin: '0 0 0.75rem', color: colors.text }}>{formatPhoneDisplay(vendor.phone) || vendor.phone}</p>
                ) : null}
                {vendor.email && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <a
                      href={`mailto:${vendor.email}`}
                      onClick={(event) => hireGate.requestContact(event, {
                        href: event.currentTarget.href,
                        vendorId: vendor.id,
                        kind: 'email',
                        onAllowed: () => trackVendorClick(vendor, 'email'),
                      })}
                      style={{ color: colors.textSecondary, fontSize: '0.9rem', textDecoration: 'none', overflowWrap: 'anywhere' }}
                    >
                      {vendor.email}
                    </a>
                  </div>
                )}
                {vendor.website && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <a
                      href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => hireGate.requestContact(event, {
                        href: event.currentTarget.href,
                        newTab: true,
                        vendorId: vendor.id,
                        kind: 'website',
                        onAllowed: () => trackVendorClick(vendor, 'website'),
                      })}
                      style={{ color: colors.textSecondary, fontSize: '0.9rem', textDecoration: 'none' }}
                    >
                      Visit Website
                    </a>
                  </div>
                )}
                <HomeownerDiscount vendor={vendor} color={colors.primary} />
                {vendor.code ? (
                  <p style={{ margin: '0.35rem 0 0', fontSize: '0.9rem', color: colors.text }}>Code: {vendor.code}</p>
                ) : null}
                {(canManageVendor(profile, vendor) || canDeleteVendor(profile, vendor)) && (
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                    {canManageVendor(profile, vendor) && (
                      <button
                        type="button"
                        style={{ ...styles.editBtn, flex: '1 1 8rem', color: colors.text, borderColor: colors.border }}
                        onClick={() => handleEdit(vendor)}
                      >
                        Edit
                      </button>
                    )}
                    {canDeleteVendor(profile, vendor) && (
                      <button
                        type="button"
                        style={{ ...styles.deleteBtn, flex: '1 1 8rem' }}
                        onClick={() => handleDelete(vendor.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </VendorTapCard>
            </div>
            );
  };

  const primaryBtn = {
    ...styles.btnPrimary,
    border: 'none',
    borderRadius: '999px',
  };
  const secondaryBtn = {
    ...styles.btnSecondary,
    background: isDark ? 'rgba(89, 196, 196, 0.18)' : 'rgba(89, 196, 196, 0.14)',
    color: isDark ? '#ccfbf1' : '#0f766e',
    border: 'none',
    borderRadius: '999px',
  };

  return (
    <div>
      <h2 data-tour="vendors" style={{...styles.pageTitle, color: colors.text, marginBottom: '1rem'}}>Vendor Management</h2>
      <div style={{ ...vendorSoftCardStyle(colors, isDark), marginBottom: '1.5rem' }}>
        <button type="button" style={primaryBtn} onClick={createVendorInvite}>Invite a vendor</button>
        {applications.length > 0 && (
          <h3 style={{ color: colors.text, margin: '1rem 0 0' }}>
            New applications ({applications.length})
          </h3>
        )}
        {inviteUrl ? (
          <div style={{ marginTop: '0.75rem' }}>
            <p style={{ margin: '0 0 0.6rem', color: colors.textSecondary, fontSize: '0.95rem' }}>
              This link works once. Create a new one for each vendor.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button type="button" style={secondaryBtn} onClick={copyInviteLink}>
                {inviteCopied ? 'Copied' : 'Copy invite message'}
              </button>
              {isNativeApp() && typeof navigator !== 'undefined' && navigator.share ? (
                <button type="button" style={secondaryBtn} onClick={shareInviteLink}>Share</button>
              ) : null}
            </div>
          </div>
        ) : null}
        {applications.map((application) => {
          const open = openApplicationId === application.id;
          const busy = applicationBusyId === application.id;
          const serves = formatServes(application) || (application.service_counties || []).concat(application.service_zips || []).filter(Boolean).join(', ');
          return (
            <div key={application.id} style={{ marginTop: '0.75rem' }}>
              <strong>{application.business_name}</strong>
              <span> — {(application.trades || []).join(', ')}{serves ? ` — ${serves}` : ''}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button type="button" style={secondaryBtn} onClick={() => setOpenApplicationId(open ? '' : application.id)}>
                  {open ? 'Hide details' : 'Details'}
                </button>
                <button type="button" style={primaryBtn} disabled={busy} onClick={() => decideApplication(application.id, 'accept')}>
                  {busy ? 'Saving...' : 'Accept'}
                </button>
                <button type="button" style={secondaryBtn} disabled={busy} onClick={() => setDeclineApplication(application)}>
                  Decline
                </button>
              </div>
              {open ? (
                <div style={{ marginTop: '0.6rem', color: colors.text, lineHeight: 1.5 }}>
                  {application.description ? <p style={{ margin: '0 0 0.35rem' }}>{application.description}</p> : null}
                  {application.email ? <p style={{ margin: '0 0 0.35rem' }}>{application.email}</p> : null}
                  {application.phone ? <p style={{ margin: '0 0 0.35rem' }}>{formatPhoneDisplay(application.phone) || application.phone}</p> : null}
                  {application.website ? <p style={{ margin: '0 0 0.35rem' }}>{application.website}</p> : null}
                  {application.hours ? <p style={{ margin: '0 0 0.35rem' }}>Hours: {application.hours}</p> : null}
                  <p style={{ margin: '0 0 0.35rem' }}>
                    After a storm: {application.storm_available === true || application.storm_available === 'yes' ? 'Yes' : 'No'}
                  </p>
                  {application.license_number ? <p style={{ margin: '0 0 0.35rem' }}>License: {application.license_number}</p> : null}
                  {application.insurance_note ? <p style={{ margin: '0 0 0.35rem' }}>Insurance: {application.insurance_note}</p> : null}
                  {application.discount ? <p style={{ margin: 0 }}>Discount: {application.discount}</p> : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={Boolean(declineApplication)}
        title="Decline this application?"
        message="They will not be added to your vendors."
        confirmLabel="Decline"
        cancelLabel="Cancel"
        busy={Boolean(declineApplication && applicationBusyId === declineApplication.id)}
        onCancel={() => setDeclineApplication(null)}
        onConfirm={() => {
          const id = declineApplication && declineApplication.id;
          setDeclineApplication(null);
          if (id) decideApplication(id, 'decline');
        }}
      />

      {viewMode === 'my' && (
        <div style={{display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center'}}>
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => {
              setShowAnalytics(!showAnalytics);
              if (!showAnalytics) loadVendorAnalytics();
            }}
          >
            {showAnalytics ? 'Hide' : 'View'} Analytics
          </button>
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => setShowCategoryModal(true)}
          >
            Manage Categories
          </button>
          <button type="button" style={secondaryBtn} onClick={() => setShowImport(true)}>
            Import CSV
          </button>
          <button type="button" style={primaryBtn} onClick={() => {
            setEditingVendor(null);
            setAdoptedProId(null);
            setAttested(false);
            setCategoryError(false);
            pendingVendorId.current = null;
            setShowModal(true);
          }}>
            Add vendor
          </button>
        </div>
      )}

      {/* View Mode Toggle. Regional browsing stays off. */}
      {REGIONAL_VENDORS_ENABLED && <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        padding: '0.5rem',
        background: colors.surfaceAlt,
        borderRadius: '8px',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => setViewMode('my')}
          style={{
            padding: '0.75rem 1.5rem',
            border: viewMode === 'my' ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
            background: viewMode === 'my' ? colors.primary : colors.surface,
            color: viewMode === 'my' ? 'white' : colors.text,
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: viewMode === 'my' ? '600' : '400',
            fontSize: '1rem',
            transition: 'all 0.2s'
          }}
        >
          Your vendors
        </button>
        <button
          onClick={() => setViewMode('regional')}
          style={{
            padding: '0.75rem 1.5rem',
            border: viewMode === 'regional' ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
            background: viewMode === 'regional' ? colors.primary : colors.surface,
            color: viewMode === 'regional' ? 'white' : colors.text,
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: viewMode === 'regional' ? '600' : '400',
            fontSize: '1rem',
            transition: 'all 0.2s'
          }}
        >
          Regional Vendors
        </button>
      </div>}

      {/* Analytics Section */}
      {showAnalytics && viewMode === 'my' && (
        <div>
          <h3 style={{...styles.sectionTitle, color: colors.text, marginBottom: '1rem'}}>
            Vendor Engagement Analytics
          </h3>
          {vendorAnalytics.length === 0 ? (
            <p style={{...styles.emptyState, color: colors.textSecondary}}>
              No vendor clicks tracked yet. Start clicking on vendor contact info to see analytics!
            </p>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {/* Total Clicks */}
                <div style={{
                  ...styles.statCard,
                  background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)'
                }}>
                  <div style={styles.statValue}>
                    {vendorAnalytics.reduce((sum, v) => sum + (v.total_clicks || 0), 0)}
                  </div>
                  <div style={styles.statLabel}>Total Clicks</div>
                </div>

                {/* Phone Clicks */}
                <div style={{
                  ...styles.statCard,
                  background: 'linear-gradient(135deg, #3a9393 0%, #2a7373 100%)'
                }}>
                  <div style={styles.statValue}>
                    {vendorAnalytics.reduce((sum, v) => sum + (v.phone_clicks || 0), 0)}
                  </div>
                  <div style={styles.statLabel}>Phone Clicks</div>
                </div>

                {/* Email Clicks */}
                <div style={{
                  ...styles.statCard,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                }}>
                  <div style={styles.statValue}>
                    {vendorAnalytics.reduce((sum, v) => sum + (v.email_clicks || 0), 0)}
                  </div>
                  <div style={styles.statLabel}>Email Clicks</div>
                </div>

                {/* Website Clicks */}
                <div style={{
                  ...styles.statCard,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                }}>
                  <div style={styles.statValue}>
                    {vendorAnalytics.reduce((sum, v) => sum + (v.website_clicks || 0), 0)}
                  </div>
                  <div style={styles.statLabel}>Website Clicks</div>
                </div>
              </div>

              {/* Top Vendors List */}
              <div style={{
                ...styles.card,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                marginBottom: '1.5rem'
              }}>
                <h4 style={{marginBottom: '1rem', color: colors.text}}>Top Vendors</h4>
                {vendorAnalytics.slice(0, 10).map((analytics, index) => (
                  <div key={analytics.vendor_id} style={{
                    padding: '1rem',
                    borderBottom: index < vendorAnalytics.length - 1 ? `1px solid ${colors.border}` : 'none',
                    background: index % 2 === 0 ? colors.surfaceAlt : 'transparent',
                    borderRadius: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                      {/* Vendor Name and Category */}
                      <div style={{width: '100%'}}>
                        <h5 style={{
                          margin: 0,
                          color: colors.text,
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal'
                        }}>
                          {analytics.vendor_name}
                        </h5>
                        <p style={{
                          margin: '0.25rem 0 0',
                          color: colors.textSecondary,
                          fontSize: '0.85rem',
                          wordWrap: 'break-word'
                        }}>
                          {analytics.category}
                        </p>
                      </div>

                      {/* Stats Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '0.75rem',
                        width: '100%'
                      }}>
                        <div style={{textAlign: 'center', padding: '0.5rem', background: colors.surface, borderRadius: '0.5rem'}}>
                          <div style={{fontWeight: '700', color: colors.text, fontSize: '1.1rem'}}>{analytics.phone_clicks || 0}</div>
                          <div style={{fontSize: '0.75rem', color: colors.textSecondary, marginTop: '0.25rem'}}>Phone</div>
                        </div>
                        <div style={{textAlign: 'center', padding: '0.5rem', background: colors.surface, borderRadius: '0.5rem'}}>
                          <div style={{fontWeight: '700', color: colors.text, fontSize: '1.1rem'}}>{analytics.email_clicks || 0}</div>
                          <div style={{fontSize: '0.75rem', color: colors.textSecondary, marginTop: '0.25rem'}}>Email</div>
                        </div>
                        <div style={{textAlign: 'center', padding: '0.5rem', background: colors.surface, borderRadius: '0.5rem'}}>
                          <div style={{fontWeight: '700', color: colors.text, fontSize: '1.1rem'}}>{analytics.website_clicks || 0}</div>
                          <div style={{fontSize: '0.75rem', color: colors.textSecondary, marginTop: '0.25rem'}}>Website</div>
                        </div>
                        <div style={{textAlign: 'center', padding: '0.5rem', background: colors.primary + '15', borderRadius: '0.5rem'}}>
                          <div style={{fontWeight: '700', color: colors.primary, fontSize: '1.1rem'}}>{analytics.total_clicks || 0}</div>
                          <div style={{fontSize: '0.75rem', color: colors.primary, marginTop: '0.25rem'}}>Total</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Your vendors View */}
      {viewMode === 'my' && (
        <>
          <VendorCategorySelect
            id="agent-vendor-category"
            value={activeFilter === 'all' ? '' : activeFilter}
            vendors={vendors}
            colors={colors}
            extraOptions={[{ value: ADDED_BY_CLIENTS_VALUE, label: ADDED_BY_CLIENTS }]}
            onChange={(value) => setActiveFilter(value || 'all')}
          />

      {showingClientPros ? (
        <ClientProsSection
          pros={clientPros}
          colors={colors}
          accent={colors.primary}
          adoptedIds={adoptedProIds}
          onAdopt={openAdopt}
        />
      ) : null}
      {!showingClientPros && vendors.some(isSettlementServiceVendor) ? (
        <p role="status" style={{ ...vendorSoftCardStyle(colors, isDark), background: colors.surfaceAlt, marginBottom: '1rem' }}>
          {SETTLEMENT_VENDOR_NOTICE}
        </p>
      ) : null}
      {showingClientPros ? null : (
        <>
          {filteredVendors.length > 0 ? (
            <div style={{ marginBottom: '1rem' }}>
              <VendorCardFootnote
                agentName={footnoteName(filteredVendors, creatorNames, [profile?.first_name, profile?.last_name].filter(Boolean).join(' '))}
                plan={profile?.subscription_plan}
                teamId={profile?.team_id}
                orgName={profile?.team_name}
                style={{ color: colors.textSecondary }}
              />
            </div>
          ) : null}
          <section aria-label={YOUR_VENDORS} style={{ marginBottom: '1.75rem', minWidth: 0 }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: colors.text }}>{YOUR_VENDORS}</h3>
            <p style={{ margin: '0 0 0.85rem', color: colors.textSecondary, lineHeight: 1.45 }}>{AGENT_YOUR_VENDORS_HELP}</p>
            {yourVendors.length === 0 ? (
              <p style={{ margin: 0, color: colors.text }}>
                {activeFilter === 'all' ? EMPTY_ADD_VENDOR : 'No vendors in this category.'}
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 17.5rem), 1fr))', alignItems: 'start', gap: '1rem' }}>
                {yourVendors.map(renderVendorCard)}
              </div>
            )}
          </section>
          {onTeam ? (
            <section aria-label={TEAM_VENDORS} style={{ marginBottom: '1.75rem', minWidth: 0 }}>
              <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: colors.text }}>{TEAM_VENDORS}</h3>
              <p style={{ margin: '0 0 0.85rem', color: colors.textSecondary, lineHeight: 1.45 }}>{TEAM_VENDORS_HELP}</p>
              {teamVendors.length === 0 ? (
                <p style={{ margin: 0, color: colors.text }}>
                  {activeFilter === 'all' ? EMPTY_TEAM_VENDORS : 'No team vendors in this category.'}
                </p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 17.5rem), 1fr))', alignItems: 'start', gap: '1rem' }}>
                  {teamVendors.map(renderVendorCard)}
                </div>
              )}
            </section>
          ) : null}
        </>
      )}
        </>
      )}

      {/* Regional Vendors View */}
      {REGIONAL_VENDORS_ENABLED && viewMode === 'regional' && (
        <>
          {/* Regional Vendor Filters */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <select
              value={regionalRegionFilter}
              onChange={(e) => setRegionalRegionFilter(e.target.value)}
              style={{
                ...styles.input,
                background: colors.surfaceAlt,
                border: `2px solid ${colors.border}`,
                color: colors.text,
                flex: 1,
                minWidth: '200px'
              }}
            >
              <option value="">All Regions</option>
              {uniqueRegions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
            <VendorCategorySelect
              id="regional-vendor-category"
              value={regionalCategoryFilter === 'all' ? '' : regionalCategoryFilter}
              vendors={regionalVendors}
              colors={colors}
              compact
              onChange={(value) => setRegionalCategoryFilter(value || 'all')}
            />
          </div>

          {/* Regional Vendor Cards */}
          <div style={{ ...styles.card, background: colors.surface, border: 'none', boxShadow: vendorSoftShadow(isDark) }}>
            {filteredRegionalVendors.length === 0 ? (
              <p style={{...styles.emptyState, color: colors.textSecondary}}>
                No regional vendors available in the selected region/category.
              </p>
            ) : (
              <>
              <VendorCardFootnote
                agentName={[profile?.first_name, profile?.last_name].filter(Boolean).join(' ')}
                plan={profile?.subscription_plan}
                teamId={profile?.team_id}
                orgName={profile?.team_name}
                style={{ color: colors.textSecondary }}
              />
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem'}}>
                {filteredRegionalVendors.map(vendor => (
                  <div key={vendor.id} style={{ ...vendorSoftCardStyle(colors, isDark), background: colors.surfaceAlt, marginBottom: 0 }}>
                    <div style={{marginBottom: '1rem'}}>
                      <h3 style={{...styles.vendorName, color: colors.text}}>{vendor.name}</h3>
                      <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem'}}>
                        <span style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                          background: '#59c4c4',
                          color: 'white',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {vendor.category}
                        </span>
                        <span style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                          background: '#8b5cf6',
                          color: 'white',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {vendor.region}
                        </span>
                        {vendor.is_recommended && (
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            background: '#f59e0b',
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 500
                          }}>
                            Recommended
                          </span>
                        )}
                      </div>
                    </div>

                    <VendorCardCopy description={vendor.description} color={colors.textSecondary} marginBottom="1rem" />

                    <div style={{fontSize: '0.9rem', color: colors.text}}>
                      {vendor.phone && (
                        <p style={{marginBottom: '0.5rem'}}>
                          <a
                            href={`tel:${vendor.phone}`}
                            onClick={(event) => hireGate.requestContact(event, {
                              href: event.currentTarget.href,
                              vendorId: vendor.id,
                              kind: 'phone',
                            })}
                            style={{color: colors.primary, textDecoration: 'none'}}
                          >
                            {formatPhoneDisplay(vendor.phone) || vendor.phone}
                          </a>
                        </p>
                      )}
                      {vendor.email && (
                        <p style={{marginBottom: '0.5rem'}}>
                          <a
                            href={`mailto:${vendor.email}`}
                            onClick={(event) => hireGate.requestContact(event, {
                              href: event.currentTarget.href,
                              vendorId: vendor.id,
                              kind: 'email',
                            })}
                            style={{color: colors.primary, textDecoration: 'none'}}
                          >
                            {vendor.email}
                          </a>
                        </p>
                      )}
                      {vendor.website && (
                        <p style={{marginBottom: '0.5rem'}}>
                          <a
                            href={`https://${vendor.website.replace(/^https?:\/\//, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => hireGate.requestContact(event, {
                              href: event.currentTarget.href,
                              newTab: true,
                              vendorId: vendor.id,
                              kind: 'website',
                            })}
                            style={{color: colors.primary, textDecoration: 'none'}}
                          >
                            Visit Website
                          </a>
                        </p>
                      )}
                      {vendor.address && (
                        <p style={{...styles.vendorContact, color: colors.textSecondary, fontSize: '0.85rem'}}>
                          {vendor.address}
                        </p>
                      )}
                      {vendor.rating && (
                        <p style={{marginTop: '0.5rem', fontWeight: '600', color: colors.text}}>
                          {vendor.rating} / 5.0 Rating
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </>
      )}

      {showImport && (
        <VendorImport
          profile={profile}
          categories={categories}
          existingVendors={vendors}
          onClose={() => setShowImport(false)}
          onFinished={async () => {
            setShowImport(false);
            await loadVendors();
            if (onStatsUpdate) onStatsUpdate();
          }}
        />
      )}

      {/* Add Vendor Modal */}
      {showModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={() => setShowModal(false)}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>{editingVendor ? 'Edit vendor' : 'Add vendor'}</h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={() => {
                setShowModal(false);
                setEditingVendor(null);
                setAttested(false);
                setCategoryError(false);
              }}>
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Vendor Name *</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData((current) => Object.assign({}, current, { name }));
                  }}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                  <label style={{...styles.label, color: colors.text, marginBottom: 0}}>
                    Categories * {formData.categories.length > 0 && `(${formData.categories.length} selected)`}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryModal(true)}
                    style={styles.btnSecondary}
                  >
                    + Add New
                  </button>
                </div>
                {categoryChips.length === 0 ? (
                  <p style={{fontSize: '0.95rem', color: colors.textSecondary, marginTop: '0.5rem'}}>
                    No categories yet. Use + Add New to create one.
                  </p>
                ) : (
                  <>
                  <input
                    type="search"
                    aria-label="Search categories"
                    placeholder="Search categories"
                    value={categoryQuery}
                    onChange={(event) => setCategoryQuery(event.target.value)}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, marginBottom: '0.6rem'}}
                  />
                  <div className="vendor-category-chips" style={{ maxHeight: '240px', overflowY: 'auto', paddingBottom: '0.15rem' }}>
                    {visibleCategoryChips.map(cat => {
                      const selected = formData.categories.includes(cat.name);
                      return (
                        <button
                          key={cat.key}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => {
                            const next = selected
                              ? formData.categories.filter(c => c !== cat.name)
                              : [...formData.categories, cat.name];
                            if (next.length > 0) setCategoryError(false);
                            setFormData({...formData, categories: next});
                          }}
                          style={{
                            boxSizing: 'border-box',
                            padding: '0.45rem 0.8rem',
                            borderRadius: '999px',
                            border: `2px solid ${selected ? '#59c4c4' : colors.border}`,
                            background: selected ? '#59c4c4' : 'transparent',
                            color: selected ? '#083838' : colors.text,
                            fontSize: '16px',
                            fontWeight: 650,
                            cursor: 'pointer',
                          }}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>
                  </>
                )}
                {categoryError && (
                  <p style={{fontSize: '0.95rem', color: '#f59e0b', marginTop: '0.5rem'}}>
                    Please select at least one category
                  </p>
                )}
              </div>

              <div style={styles.formGroup}>
                <ServiceAreaFields
                  value={formData}
                  colors={colors}
                  error={areaError}
                  onChange={(areaValue) => {
                    setAreaError('');
                    setFormData((current) => ({ ...current, ...areaValue }));
                  }}
                />
              </div>

              {/* Visibility selector - only show if user is on a team */}
              {profile.team_id && (
                <div style={styles.formGroup}>
                  <label style={{...styles.label, color: colors.text}}>Visibility *</label>
                  <select
                    value={formData.visibility}
                    onChange={(e) => setFormData({...formData, visibility: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                    required
                  >
                    <option value="personal">Your vendors</option>
                    <option value="team">Team vendors</option>
                  </select>
                  <p style={{fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.25rem'}}>
                    {formData.visibility === 'personal'
                      ? 'Your homeowners see this. Other agents on your team do not.'
                      : 'Your team and their homeowners see this.'}
                  </p>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Discount Offer</label>
                <input
                  type="text"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  placeholder="e.g., 15% off first service"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Discount Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  placeholder="e.g., PACARION15"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, minHeight: '100px'}}
                  placeholder="Brief description of services"
                />
              </div>

              {adoptedProId && !editingVendor ? <AdoptNotice /> : null}
              <VendorAttestationField checked={attested} onChange={setAttested} color={colors.text} />

              <div style={{marginTop: '1.5rem'}}>
                <button type="submit" style={styles.btnPrimary}>
                  {editingVendor ? 'Update vendor' : 'Add vendor'}
                </button>
                <button
                  type="button"
                  style={{...styles.btnPrimary, background: '#666', marginLeft: '0.5rem'}}
                  onClick={() => {
                    setShowModal(false);
                    setEditingVendor(null);
                    setAttested(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </ModalLayer>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
          setNewCategoryName('');
        }}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>Manage Vendor Categories</h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={() => {
                setShowCategoryModal(false);
                setEditingCategory(null);
                setNewCategoryName('');
              }}>
                ✕
              </button>
            </div>

            <div style={styles.formGroup}>
              <label style={{...styles.label, color: colors.text}}>
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </label>
              <div style={{display: 'flex', gap: '0.5rem'}}>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Category name..."
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, flex: 1}}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <button
                  style={styles.btnPrimary}
                  onClick={handleAddCategory}
                >
                  {editingCategory ? 'Update' : 'Add'}
                </button>
                {editingCategory && (
                  <button
                    style={{...styles.btnPrimary, background: '#666'}}
                    onClick={() => {
                      setEditingCategory(null);
                      setNewCategoryName('');
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div style={{marginTop: '1.5rem'}}>
              <h3 style={{marginBottom: '1rem', color: colors.text}}>Your Categories ({categories.length})</h3>
              <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                {categories.length === 0 ? (
                  <p style={{...styles.emptyState, color: colors.textSecondary}}>No categories yet. Add your first category above!</p>
                ) : (
                  categories.map(category => (
                    <div key={category.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      padding: '0.75rem',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '0.5rem',
                      marginBottom: '0.5rem',
                      background: colors.surfaceAlt,
                      minWidth: 0,
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <span style={{fontSize: '1rem', color: colors.text}}>{category.name}</span>
                      <div style={{display: 'flex', gap: '0.5rem'}}>
                        <button
                          type="button"
                          style={{...styles.editBtn, color: colors.text, borderColor: colors.border}}
                          onClick={() => handleEditCategory(category)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={styles.deleteBtn}
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </ModalLayer>
      )}
      <BeforeYouHireModal
        open={!!hireGate.pending}
        saving={hireGate.saving}
        error={hireGate.error}
        onAccept={hireGate.accept}
        onDismiss={hireGate.dismiss}
      />
    </div>
  );
};

const styles = {
  pageTitle: {
    fontSize: 'clamp(1.5rem, 5vw, 2rem)',
    fontWeight: '600',
    margin: 0
  },
  sectionTitle: {
    fontSize: 'clamp(1.1rem, 4vw, 1.3rem)',
    fontWeight: '600'
  },
  statCard: {
    padding: '2rem',
    borderRadius: '1rem',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minHeight: '120px',
    transition: 'transform 0.2s',
    cursor: 'default'
  },
  statValue: {
    fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
    fontWeight: '700',
    marginBottom: '0.5rem'
  },
  statLabel: {
    fontSize: 'clamp(0.85rem, 3vw, 1rem)',
    opacity: 0.95,
    fontWeight: '500',
    textAlign: 'center'
  },
  tabs: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    borderBottom: '2px solid #eee',
    flexWrap: 'wrap'
  },
  tab: {
    padding: '1rem 1.5rem',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    fontWeight: 600,
    color: '#666'
  },
  tabActive: {
    color: '#59c4c4',
    borderBottomColor: '#59c4c4'
  },
  card: {
    background: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  vendorContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  vendorName: {
    marginBottom: '0.5rem',
    fontSize: '1.2rem'
  },
  vendorMeta: {
    color: '#666',
    marginBottom: '0.5rem'
  },
  vendorContact: {
    marginBottom: '0.5rem'
  },
  discountBox: {
    background: '#fff3cd',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    marginTop: '0.5rem'
  },
  emptyState: {
    color: '#666',
    textAlign: 'center',
    padding: '2rem'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: '#083838',
    border: 'none',
    padding: '0.75rem 1.15rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '16px'
  },
  btnSecondary: {
    background: 'transparent',
    color: '#59c4c4',
    border: '2px solid #59c4c4',
    padding: '0.65rem 1rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: '650',
    fontSize: '16px'
  },
  editBtn: {
    background: 'transparent',
    color: 'inherit',
    border: '1px solid currentColor',
    padding: '0.35rem 0.7rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    minHeight: '36px'
  },
  deleteBtn: {
    background: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    padding: '0.35rem 0.7rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '650',
    minHeight: '36px'
  },
  modal: {
    display: 'flex',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    fontSize: '1rem'
  },
  tableHeader: {
    textAlign: 'left',
    padding: '0.75rem',
    fontWeight: '600',
    fontSize: '0.9rem'
  },
  tableCell: {
    padding: '0.75rem',
    fontSize: '0.95rem'
  }
};

export default VendorManagement;
